'use client';

import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDown, Play } from 'lucide-react';
import { createPortal } from 'react-dom';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vs } from 'react-syntax-highlighter/dist/esm/styles/prism';
import remarkGfm from 'remark-gfm';
import { apiFetch, apiStreamFetch } from '@/lib/api/apiClient';
import { useAuthStore } from '@/stores/useAuthStore';
import { type WhiteSize, useBoardEditorConfigStore } from '@/stores/useBoardEditorConfigStore';
import { useBoardEditorFileStore } from '@/stores/useBoardEditorFileStore';
import { useBoardEditorStore } from '@/stores/useBoardEditorStore';
import IconAdaptive from '@/components/IconAdaptive';
import ImageAdaptive from '@/components/ImageAdaptive';
import BoardAIScoreOverlay from '@/(routers)/boards/editor/components/unit/BoardAIScoreOverlay';

type CharacterState = 'idle' | 'talking' | 'thinking' | 'happy';

interface ChatMessage {
    id: string;
    role: 'user' | 'ai';
    text: string;
    isStreaming?: boolean;
    extractedCode?: string;
}

interface EDIYScore {
    score_daily: number;
    score_specialized: number;
    score_software: number;
    score_hardware: number;
    score_innovation: number;
    score_versatile: number;
}

interface ScoreResult {
    ediy_score: EDIYScore;
    code_score: number;
    comment: string;
}

type SuggestChip = {
    label: string;
    bg: string;
    children?: { label: string }[];
};

const CHARACTER_IMAGES: Record<CharacterState, string> = {
    idle: '/images/ai/white-01.png',
    thinking: '/images/ai/white-02.png',
    talking: '/images/ai/white-03.png',
    happy: '/images/ai/white-04.png',
};

const CHAR_COL_WIDTH = 140;

const CHAR_SIZE_MAP: Record<WhiteSize, number> = {
    micro: 60,
    small: 80,
    medium: 95,
    large: 110,
};

const SUGGEST_CHIPS: SuggestChip[] = [
    { label: '何からはじめる？', bg: '#ef4444' },
    { label: 'ブロックのこと教えて', bg: '#eab308' },
    { label: 'プログラムを解説して', bg: '#22c55e' },
    { label: 'このエラー何？', bg: '#ec4899' },
    { label: '書き込みできない', bg: '#8b5cf6' },
    {
        label: 'つくってほしい',
        bg: '#f97316',
        children: [{ label: 'Lチカをつくって' }, { label: '明るさをしらべて' }],
    },
    { label: '豆知識を教えて', bg: '#0ea5e9' },
];

const cleanText = (text: string) =>
    text
        .replace(/\n{3,}/g, '\n\n')
        .replace(/…/g, '...')
        .replace(/——/g, '--')
        .replace(/[""]/g, '"')
        .replace(/['']/g, "'")
        .replace(/((?:^|\n)[*\-] .+)(\n)(?![*\-\n])/g, '$1\n\n')
        .replace(/((?:^|\n)\d+\. .+)(\n)(?!\d+\.|\n)/g, '$1\n\n')
        .trim();

function extractCodeBlock(text: string): string | undefined {
    const match = text.match(/```(?:cpp|arduino|c)?\n([\s\S]*?)```/);
    const code = match?.[1]?.trim();
    if (!code) return undefined;
    if (!code.includes('void setup()')) return undefined;
    return code;
}

function TypingDots() {
    return (
        <span className="inline-flex items-center gap-1 ml-1">
            {[0, 1, 2].map((i) => (
                <motion.span
                    key={i}
                    className="w-[5px] h-[5px] rounded-full inline-block bg-sky-400"
                    animate={{ opacity: [0.3, 1, 0.3], y: [0, -3, 0] }}
                    transition={{ duration: 0.8, repeat: Infinity, delay: i * 0.15 }}
                />
            ))}
        </span>
    );
}

interface ChipPopoverProps {
    chip: SuggestChip;
    anchorRect: DOMRect;
    onSelect: (label: string) => void;
    onClose: () => void;
}

function ChipPopover({ chip, anchorRect, onSelect, onClose }: ChipPopoverProps) {
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) onClose();
        };
        document.addEventListener('click', handler);
        return () => document.removeEventListener('click', handler);
    }, [onClose]);

    const top = anchorRect.top + anchorRect.height / 2 - 15;
    const left = anchorRect.right + 15;

    return createPortal(
        <motion.div
            ref={ref}
            initial={{ opacity: 0, x: -8, scale: 0.96 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: -8, scale: 0.96 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className="fixed z-[9999] py-1.5 px-1.5 rounded-2xl shadow-xl"
            style={{
                top,
                left,
                transform: 'translateY(-50%)',
                background: 'rgba(255,255,255,0.92)',
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
                border: `1.5px solid ${chip.bg}30`,
                boxShadow: `0 8px 10px ${chip.bg}22, 0 2px 8px rgba(0,0,0,0.08)`,
                minWidth: 130,
            }}
        >
            <div className="flex flex-col gap-0.5">
                {chip.children?.map((child) => (
                    <button
                        key={child.label}
                        onClick={() => onSelect(child.label)}
                        className="font-corp font-medium text-[12px] px-3 py-1.5 rounded-xl text-left transition-all active:scale-95"
                        style={{ color: chip.bg }}
                        onMouseEnter={(e) => {
                            (e.currentTarget as HTMLButtonElement).style.background = chip.bg + '12';
                        }}
                        onMouseLeave={(e) => {
                            (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                        }}
                    >
                        {child.label}
                    </button>
                ))}
            </div>
        </motion.div>,
        document.body,
    );
}

function AiBubbleContent({
    text,
    isStreaming,
    isLatest,
    extractedCode,
    onApplyCode,
    onApplyToBlocks,
    isDark,
}: {
    text: string;
    isStreaming?: boolean;
    isLatest?: boolean;
    extractedCode?: string;
    onApplyCode?: () => void;
    onApplyToBlocks?: () => void;
    isDark?: boolean;
}) {
    const buttonContainerRef = useRef<HTMLDivElement>(null);
    const [stackButtons, setStackButtons] = useState(false);

    useEffect(() => {
        const el = buttonContainerRef.current;
        if (!el) return;
        const observer = new ResizeObserver(([entry]) => {
            setStackButtons(entry.contentRect.width <= 305);
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, [extractedCode]);

    const latestTextColor = isDark
        ? '[&_p]:text-gray-100 [&_li]:text-gray-100 [&_strong]:text-sky-400'
        : '[&_p]:text-color-block-ai-label-dark [&_li]:text-color-block-ai-label-dark [&_strong]:text-orange-500';

    const oldTextColor = isDark
        ? '[&_p]:text-gray-500 [&_li]:text-gray-500 [&_strong]:text-gray-600'
        : '[&_p]:text-gray-400 [&_li]:text-gray-400 [&_strong]:text-orange-500';

    return (
        <div
            className={`prose prose-sm max-w-none
                [&_pre]:bg-transparent
                [&>p]:mb-1 [&>p:last-child]:mb-0
                [&>ul]:mb-1 [&>ul]:pl-4
                [&>ol]:mb-1 [&>ol]:pl-4
                [&>h1]:text-[15px] [&>h2]:text-[14px] [&>h3]:text-[13px]
                [&_table]:w-full [&_table]:text-[11px] [&_table]:border-collapse
                [&_th]:bg-sky-50 [&_th]:text-sky-700 [&_th]:px-2 [&_th]:py-1 [&_th]:border [&_th]:border-sky-100
                [&_td]:px-2 [&_td]:py-1 [&_td]:border [&_td]:border-gray-100
                [&_hr]:hidden
                ${isLatest ? latestTextColor : oldTextColor}
            `}
        >
            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                    h1({ children }) {
                        return <p className="text-[15px]">{children}</p>;
                    },
                    h2({ children }) {
                        return <p className="text-[14px]">{children}</p>;
                    },
                    h3({ children }) {
                        return <p className="text-[13px]">{children}</p>;
                    },
                    code({ className, children, ...props }) {
                        const match = /language-(\w+)/.exec(className || '');
                        if (match) {
                            return (
                                <div style={{ width: 0, minWidth: '100%', overflowX: 'auto' }}>
                                    <SyntaxHighlighter
                                        style={vs}
                                        language={match[1]}
                                        PreTag="div"
                                        customStyle={{
                                            borderRadius: '8px',
                                            fontSize: '13px',
                                            margin: '4px 0',
                                            border: 'none',
                                            outline: 'none',
                                            boxShadow: 'none',
                                            background: '#f6f8fa',
                                            padding: '12px 16px',
                                        }}
                                    >
                                        {String(children).replace(/\n$/, '')}
                                    </SyntaxHighlighter>
                                </div>
                            );
                        }
                        return (
                            <span style={{ display: 'block', width: 0, minWidth: '100%', overflowX: 'auto' }}>
                                <code
                                    className="block whitespace-nowrap text-sky-800 px-3 py-2 my-1 rounded-lg bg-sky-50 text-[12px] font-mono border border-sky-100"
                                    {...props}
                                >
                                    {children}
                                </code>
                            </span>
                        );
                    },
                }}
            >
                {text}
            </ReactMarkdown>
            {isStreaming && <TypingDots />}
            {!isStreaming && extractedCode && onApplyCode && (
                <div
                    ref={buttonContainerRef}
                    className={`flex mt-3 pt-3 border-t border-sky-200 gap-2 ${
                        stackButtons ? 'flex-col items-stretch' : 'flex-row justify-center'
                    }`}
                >
                    {onApplyToBlocks && (
                        <button
                            onClick={onApplyToBlocks}
                            className="whitespace-nowrap flex items-center justify-center gap-1.5 bg-color-ide-editor-label-block hover:opacity-80 active:scale-95 text-white text-[13px] font-corp font-medium px-5 py-2 rounded-full transition-all shadow-sm"
                        >
                            <IconAdaptive Icon={Play} strokeWidth={1.5} baseSize={9} fill="white" />
                            ブロックに反映（β版）
                        </button>
                    )}
                    <button
                        onClick={onApplyCode}
                        className="whitespace-nowrap flex items-center justify-center gap-1.5 bg-color-ide-editor-label-code hover:opacity-80 active:scale-95 
                        text-white text-[13px] font-corp font-medium px-5 py-2 rounded-full transition-all shadow-sm"
                    >
                        <IconAdaptive Icon={Play} strokeWidth={1.5} baseSize={9} fill="white" />
                        コードに反映
                    </button>
                </div>
            )}
        </div>
    );
}

export default function BoardAIAssistant() {
    const code = useBoardEditorStore((s) => s.code);
    const board = useBoardEditorStore((s) => s.board);
    const setCode = useBoardEditorStore((s) => s.setCode);
    const setEditorMode = useBoardEditorStore((s) => s.setEditorMode);
    const { currentFileId, markAsDirty, updateFile, files } = useBoardEditorFileStore();

    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [input, setInput] = useState('');
    const [charState, setCharState] = useState<CharacterState>('idle');
    const [isLoading, setIsLoading] = useState(false);
    const [scoreResult, setScoreResult] = useState<ScoreResult | null>(null);
    const [isScoring, setIsScoring] = useState(false);
    const [showScore, setShowScore] = useState(false);
    const [popover, setPopover] = useState<{ chip: SuggestChip; anchorRect: DOMRect } | null>(null);
    const [visibleChipCount, setVisibleChipCount] = useState(SUGGEST_CHIPS.length);
    const [containerHeight, setContainerHeight] = useState(0);
    const [containerWidth, setContainerWidth] = useState(0);

    const chatBottomRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const hasGreetedRef = useRef(false);
    const charColRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const isDark = useBoardEditorConfigStore((s) => s.isDark);

    const whiteSize = useBoardEditorConfigStore((s) => s.whiteSize);
    const charSize = CHAR_SIZE_MAP[whiteSize];

    const getBlockContent = () => {
        const file = files.find((f) => f.id === currentFileId);
        return file?.blockContent ?? '';
    };

    useEffect(() => {
        chatBottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // コンテナサイズ監視（BoardAIScoreOverlay に渡すため）
    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const ro = new ResizeObserver(([entry]) => {
            setContainerHeight(entry.contentRect.height);
            setContainerWidth(entry.contentRect.width);
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    useEffect(() => {
        const el = charColRef.current;
        if (!el) return;
        const observer = new ResizeObserver(([entry]) => {
            const availableHeight = entry.contentRect.height;
            if (availableHeight === 0) return;
            const charAreaHeight = charSize * 1.4 + 8 + 16;
            const chipsHeight = availableHeight - charAreaHeight;
            if (chipsHeight <= 0) return;
            const chipHeight = 34;
            const count = Math.max(1, Math.floor(chipsHeight / chipHeight));
            setVisibleChipCount(Math.min(count, SUGGEST_CHIPS.length));
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, [charSize]);

    const nickname = useAuthStore((s) => s.userAuth?.nickname ?? null);
    const hydration = useAuthStore((s) => s.hydration);

    useEffect(() => {
        if (hydration !== 'hydrated') return;
        if (hasGreetedRef.current) return;
        hasGreetedRef.current = true;
        const greet = async () => {
            setIsLoading(true);
            setCharState('thinking');
            const id = `${Date.now()}-greet`;
            setMessages([{ id, role: 'ai', text: '', isStreaming: true }]);
            let accumulated = '';
            try {
                await apiStreamFetch(
                    '/boards/editor/ai/chat/stream',
                    {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            code_content: code,
                            block_content: getBlockContent(),
                            board_type: board,
                            user_nickname: nickname,
                            message: '',
                            mode: 'greet',
                        }),
                    },
                    {
                        onProgress: (data) => {
                            if (data.text) {
                                accumulated += data.text;
                                setCharState('talking');
                                setMessages([{ id, role: 'ai', text: accumulated, isStreaming: true }]);
                            }
                        },
                        onComplete: (data) => {
                            if (data.text) accumulated += data.text;
                            const cleaned = cleanText(accumulated);
                            setMessages([{ id, role: 'ai', text: cleaned, isStreaming: false }]);
                            setCharState('happy');
                            setTimeout(() => setCharState('idle'), 3000);
                        },
                        onError: () => {
                            setMessages([{ id, role: 'ai', text: 'おお、よく来たのぉ。', isStreaming: false }]);
                            setCharState('idle');
                        },
                    },
                );
            } finally {
                setIsLoading(false);
            }
        };
        greet();
    }, [hydration]);

    useEffect(() => {
        const handler = (e: Event) => {
            const detail = (e as CustomEvent).detail;
            if (detail?.success) triggerScore();
        };
        window.addEventListener('board-upload-success', handler);
        return () => window.removeEventListener('board-upload-success', handler);
    }, [code, board, currentFileId, files]);

    const addMessage = (role: 'user' | 'ai', text: string, isStreaming = false): string => {
        const id = `${Date.now()}-${Math.random()}`;
        setMessages((prev) => [...prev, { id, role, text, isStreaming }]);
        return id;
    };

    const updateMessage = (id: string, text: string, isStreaming: boolean, extractedCode?: string) =>
        setMessages((prev) =>
            prev.map((m) =>
                m.id === id
                    ? { ...m, text, isStreaming, ...(extractedCode !== undefined ? { extractedCode } : {}) }
                    : m,
            ),
        );

    const buildHistory = () => {
        return messages
            .filter((m) => !m.isStreaming)
            .slice(-20)
            .map((m) => ({
                role: m.role === 'ai' ? 'assistant' : 'user',
                content: m.text,
            }));
    };

    const handleApplyCode = (extractedCode: string) => {
        setCode(extractedCode);
        setEditorMode('code');
        if (currentFileId) {
            const currentFile = files.find((f) => f.id === currentFileId);
            markAsDirty(currentFileId);
            updateFile(currentFileId, {
                codeContent: extractedCode,
                isBlockSynced: false,
                isCodeEditConfirmed: true,
                ...(currentFile?.isBlockSynced ? { preEditBlockContent: currentFile.blockContent } : {}),
            });
        }
    };

    const handleApplyToBlocks = async (extractedCode: string) => {
        setIsLoading(true);
        setCharState('thinking');
        const aiMsgId = addMessage('ai', 'ブロックに変換中じゃ...', true);

        try {
            const res = await apiFetch('/boards/editor/ai/to-blocks', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    code_content: extractedCode,
                    board_type: board,
                    message_history: buildHistory(),
                }),
            });

            setCode(res.converted_code);
            if (currentFileId) {
                markAsDirty(currentFileId);
                updateFile(currentFileId, {
                    codeContent: res.converted_code,
                    blockContent: res.xml,
                    isBlockSynced: true,
                    isCodeEditConfirmed: false,
                    preEditBlockContent: '',
                });
            }

            setEditorMode('block');
            await new Promise((resolve) => setTimeout(resolve, 150));
            window.dispatchEvent(new CustomEvent('blockly-restore'));

            const commentLines = res.unsupported_comments ?? [];
            const commentText =
                commentLines.length > 0
                    ? `ブロックへの変換が完了したぞ！\nただし、以下の処理はブロックでは表現できなかったため省いておる：\n${commentLines
                          .map((c: string) => `- ${c}`)
                          .join('\n')}`
                    : 'ブロックへの変換が完了したぞ！全ての処理をブロックで表現できたようじゃ。動作を確認してみてくれ。思った通りに動いておるかの？';

            updateMessage(aiMsgId, commentText, false);
            setCharState('happy');
            setTimeout(() => setCharState('idle'), 3000);
        } catch (e) {
            updateMessage(aiMsgId, 'ブロックへの変換に失敗してしもうた。もう一度試してみなさい。', false);
            setCharState('idle');
        } finally {
            setIsLoading(false);
        }
    };

    const handleSend = async (overrideMessage?: string) => {
        const text = (overrideMessage ?? input).trim();
        if (!text || isLoading) return;
        setInput('');
        setPopover(null);
        addMessage('user', text);
        setIsLoading(true);
        setCharState('thinking');
        const aiMsgId = addMessage('ai', '', true);
        let accumulated = '';
        try {
            await apiStreamFetch(
                '/boards/editor/ai/chat/stream',
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        code_content: code,
                        block_content: getBlockContent(),
                        board_type: board,
                        user_nickname: nickname,
                        message: text,
                        mode: 'chat',
                        message_history: buildHistory(),
                    }),
                },
                {
                    onProgress: (data) => {
                        if (data.text) {
                            accumulated += data.text;
                            setCharState('talking');
                            updateMessage(aiMsgId, accumulated, true);
                        }
                    },
                    onComplete: (data) => {
                        if (data.text) accumulated += data.text;
                        const cleaned = cleanText(accumulated);
                        const extractedCode = extractCodeBlock(cleaned);
                        updateMessage(aiMsgId, cleaned, false, extractedCode);
                        setCharState('happy');
                        setTimeout(() => setCharState('idle'), 3000);
                        setTimeout(() => inputRef.current?.focus(), 50);
                    },
                    onError: () => {
                        updateMessage(
                            aiMsgId,
                            'すまんが、この後来客の予定があっての。スクーミーの話は時間をあけてまたあとにしよう。楽しみにしとるぞ。',
                            false,
                        );
                        setCharState('idle');
                    },
                },
            );
        } finally {
            setIsLoading(false);
        }
    };

    const triggerScore = async () => {
        if (!currentFileId || !code) return;

        const currentFile = files.find((f) => f.id === currentFileId);
        if (!currentFile) {
            console.warn('[BoardAIAssistant] File not found for scoring:', currentFileId);
            setIsScoring(false);
            return;
        }

        setIsScoring(true);
        setShowScore(true);
        setCharState('thinking');
        try {
            const res = await apiFetch('/boards/editor/ai/score', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    code_content: code,
                    board_type: board,
                    resource_id: Number(currentFileId),
                    block_content: getBlockContent(),
                    is_block_synced: currentFile.isBlockSynced,
                }),
            });
            setScoreResult(res);
            setCharState('happy');
            setTimeout(() => setCharState('idle'), 3000);
        } catch {
            setCharState('idle');
            setShowScore(false);
        } finally {
            setIsScoring(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const handleChipClick = (chip: SuggestChip, e: React.MouseEvent<HTMLButtonElement>) => {
        if (!chip.children) {
            handleSend(chip.label);
            return;
        }
        if (popover?.chip.label === chip.label) {
            setPopover(null);
            return;
        }
        setPopover({ chip, anchorRect: e.currentTarget.getBoundingClientRect() });
    };

    return (
        <div
            ref={containerRef}
            className={`flex flex-col w-full h-full overflow-hidden relative ${isDark ? 'bg-[#111111]' : 'bg-white'}`}
        >
            <div className="flex flex-1 min-h-0 overflow-hidden">
                <div
                    ref={charColRef}
                    className="flex-shrink-0 flex flex-col items-center pb-2 pt-2"
                    style={{ width: CHAR_COL_WIDTH }}
                >
                    <div className="flex flex-col items-center gap-1.5 w-full px-1">
                        {SUGGEST_CHIPS.slice(0, visibleChipCount).map((chip) => (
                            <button
                                key={chip.label}
                                onClick={(e) => handleChipClick(chip, e)}
                                disabled={isLoading}
                                className="flex items-center justify-center gap-1 font-corp font-medium text-[13px] px-2 py-1 rounded-full active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed transition-all w-full"
                                style={{
                                    color: chip.bg,
                                    background: popover?.chip.label === chip.label ? chip.bg + '28' : chip.bg + '18',
                                }}
                            >
                                <span>{chip.label}</span>
                                {chip.children && (
                                    <motion.span
                                        animate={{ rotate: popover?.chip.label === chip.label ? -90 : 0 }}
                                        transition={{ duration: 0.2 }}
                                        className="text-[8px] flex-shrink-0 opacity-60"
                                        style={{ color: chip.bg }}
                                    >
                                        <IconAdaptive Icon={ChevronDown} strokeWidth={1.5} baseSize={18} />
                                    </motion.span>
                                )}
                            </button>
                        ))}
                    </div>

                    <AnimatePresence mode="wait">
                        <motion.div
                            key={charState}
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.8 }}
                            transition={{ duration: 0.15 }}
                            style={{ width: charSize, height: Math.round(charSize * 1.4), marginBottom: '8px' }}
                            className="mt-auto flex-shrink-0"
                        >
                            <ImageAdaptive
                                src={CHARACTER_IMAGES[charState]}
                                baseWidth={charSize}
                                className="object-contain w-full h-full"
                            />
                        </motion.div>
                    </AnimatePresence>
                </div>

                <div className="flex-1 overflow-x-hidden overflow-y-auto px-3 pt-3 pb-2 min-h-0 flex flex-col min-w-0">
                    <div className="flex-1 min-h-0" />
                    <div className="space-y-4 w-full">
                        {messages.map((msg, index) => {
                            const isLatestAi = msg.role === 'ai' && index === messages.length - 1;
                            const text = cleanText(msg.text);
                            return (
                                <motion.div
                                    key={msg.id}
                                    initial={{ opacity: 0, y: 8 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.15 }}
                                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                                >
                                    <div
                                        className={`flex flex-col ${
                                            msg.role === 'user' ? 'items-end' : 'items-start'
                                        } max-w-[95%] min-w-0`}
                                    >
                                        <div
                                            className={`px-3 py-2 rounded-2xl font-corp font-medium tracking-wider leading-relaxed break-words shadow-md overflow-x-hidden ${
                                                msg.role === 'user'
                                                    ? 'bg-sky-500 text-white rounded-br-sm text-[13px]'
                                                    : isLatestAi
                                                    ? isDark
                                                        ? 'bg-[#2d2d2d] border-2 border-sky-500 rounded-bl-sm'
                                                        : 'bg-color-block-ai-label-light border-2 border-color-block-ai-label rounded-bl-sm'
                                                    : isDark
                                                    ? 'bg-[#252525] border border-gray-700 text-gray-500'
                                                    : 'bg-white border border-gray-100 text-gray-400'
                                            }`}
                                        >
                                            {msg.role === 'user' ? (
                                                text || (msg.isStreaming ? <TypingDots /> : '')
                                            ) : text ? (
                                                <AiBubbleContent
                                                    text={text}
                                                    isStreaming={msg.isStreaming}
                                                    isLatest={isLatestAi}
                                                    extractedCode={msg.extractedCode}
                                                    isDark={isDark}
                                                    onApplyCode={
                                                        msg.extractedCode
                                                            ? () => handleApplyCode(msg.extractedCode!)
                                                            : undefined
                                                    }
                                                    onApplyToBlocks={
                                                        msg.extractedCode
                                                            ? () => handleApplyToBlocks(msg.extractedCode!)
                                                            : undefined
                                                    }
                                                />
                                            ) : msg.isStreaming ? (
                                                <TypingDots />
                                            ) : (
                                                ''
                                            )}
                                        </div>
                                        {isLatestAi && (
                                            <p
                                                className={`font-corp font-medium text-[11px] mt-2 ml-1 ${
                                                    isDark ? 'text-sky-600' : 'text-color-block-ai-label'
                                                }`}
                                            >
                                                ホワイト先生
                                            </p>
                                        )}
                                    </div>
                                </motion.div>
                            );
                        })}
                        <div ref={chatBottomRef} />
                    </div>
                </div>
            </div>

            <div
                className={`flex-shrink-0 px-3 pb-3 pt-2 flex gap-2 items-end border-t-2 ${
                    isDark ? 'border-gray-700' : 'border-gray-50'
                }`}
            >
                <textarea
                    ref={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    disabled={isLoading}
                    rows={2}
                    placeholder="スクーミーボード、センサーコネクター、プログラムなど、わからないことは、まずホワイト先生に聞いてみよう！"
                    className={`font-notojp font-medium text-[13px] placeholder:text-[12px] flex-1 resize-none rounded-xl px-3 py-2 outline-none border-2 transition-colors disabled:opacity-50 ${
                        isDark
                            ? 'bg-[#2d2d2d] text-gray-200 placeholder-gray-500 border-gray-600 focus:border-sky-500'
                            : 'bg-gray-50 text-gray-700 placeholder-gray-400 border-gray-100 focus:border-sky-300'
                    }`}
                />
                <button
                    onClick={() => handleSend()}
                    disabled={isLoading || !input.trim()}
                    className="w-10 h-10 flex-shrink-0 rounded-xl bg-sky-500 hover:opacity-80 active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed transition-all flex items-center justify-center shadow-md"
                >
                    <svg viewBox="0 0 24 24" className="w-4 h-4 fill-white">
                        <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                    </svg>
                </button>
            </div>

            {/* show=false時は完全にアンマウントする */}
            <AnimatePresence>
                {showScore && (
                    <BoardAIScoreOverlay
                        show={showScore}
                        isScoring={isScoring}
                        scoreResult={scoreResult}
                        onClose={() => setShowScore(false)}
                        containerHeight={containerHeight}
                        containerWidth={containerWidth}
                        isDark={isDark}
                    />
                )}
            </AnimatePresence>

            <AnimatePresence>
                {popover && (
                    <ChipPopover
                        chip={popover.chip}
                        anchorRect={popover.anchorRect}
                        onSelect={(label) => handleSend(label)}
                        onClose={() => setPopover(null)}
                    />
                )}
            </AnimatePresence>
        </div>
    );
}
